import { useState } from 'react';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { RequireShop } from '../../components/ShopSelector';
import { ErrorBanner, PageHeader } from '../../components/Layout';
import { useShop } from '../../context/ShopContext';

const emptyInvoice = { order_id: '', notes: '' };
const emptyPayment = { invoice_id: '', amount: '', method: 'CREDIT_CARD', transaction_id: '' };

export default function BillingPage() {
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const [invoiceForm, setInvoiceForm] = useState(emptyInvoice);
  const [paymentForm, setPaymentForm] = useState(emptyPayment);
  const [invoice, setInvoice] = useState(null);
  const [payment, setPayment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleCreateInvoice = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setPayment(null);
    try {
      const token = await getToken();
      const created = await shopApi.createInvoice(token, selectedShopId, {
        order_id: Number(invoiceForm.order_id),
        notes: invoiceForm.notes || null,
      });
      setInvoice(created);
      setPaymentForm((prev) => ({ ...prev, invoice_id: String(created.id), amount: String(created.total_amount) }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePayment = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const created = await shopApi.createPayment(token, selectedShopId, {
        invoice_id: Number(paymentForm.invoice_id),
        amount: Number(paymentForm.amount),
        method: paymentForm.method,
        transaction_id: paymentForm.transaction_id || null,
      });
      setPayment(created);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RequireShop>
      <PageHeader title="Billing" description="Create invoices from orders and record payments." />
      <ErrorBanner message={error} />

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Create invoice</h3>
          <form className="form-grid" onSubmit={handleCreateInvoice}>
            <div className="form-field">
              <label htmlFor="invoice-order">Order ID</label>
              <input
                id="invoice-order"
                type="number"
                value={invoiceForm.order_id}
                onChange={(e) => setInvoiceForm((p) => ({ ...p, order_id: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="invoice-notes">Notes</label>
              <input
                id="invoice-notes"
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create invoice'}
            </button>
          </form>
          {invoice && (
            <p className="dns-record-hint" style={{ marginTop: '1rem' }}>
              Invoice <strong>{invoice.invoice_number}</strong> · {invoice.currency}{' '}
              {invoice.total_amount} · order #{invoice.order_id}
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Record payment</h3>
          <form className="form-grid" onSubmit={handleCreatePayment}>
            <div className="form-field">
              <label htmlFor="payment-invoice">Invoice ID</label>
              <input
                id="payment-invoice"
                type="number"
                value={paymentForm.invoice_id}
                onChange={(e) => setPaymentForm((p) => ({ ...p, invoice_id: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="payment-amount">Amount</label>
              <input
                id="payment-amount"
                type="number"
                min="0"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="payment-method">Method</label>
              <select
                id="payment-method"
                value={paymentForm.method}
                onChange={(e) => setPaymentForm((p) => ({ ...p, method: e.target.value }))}
              >
                <option value="CREDIT_CARD">Credit card</option>
                <option value="CASH_ON_DELIVERY">Cash on delivery</option>
                <option value="BANK_TRANSFER">Bank transfer</option>
                <option value="PAYPAL">PayPal</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="payment-txn">Transaction ID</label>
              <input
                id="payment-txn"
                value={paymentForm.transaction_id}
                onChange={(e) => setPaymentForm((p) => ({ ...p, transaction_id: e.target.value }))}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Record payment'}
            </button>
          </form>
          {payment && (
            <p className="dns-record-hint" style={{ marginTop: '1rem' }}>
              Payment recorded · {payment.currency} {payment.amount} · {payment.status}
            </p>
          )}
        </div>
      </div>
    </RequireShop>
  );
}
